import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, Button, Box, Typography, Grid, CircularProgress } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";

import CustomSelect from "../../../../FormFields/CustomSelect";
import CustomSwitch from "../../../../FormFields/CustomSwitch";
import CustomDate from "../../../../CustomForm/CustomDate";
import CustomTextArea from "../../../../CustomForm/CustomTextArea";
import {ClientSelect} from "../../../ClientSelect";
import EmployeeSearch from "../../../../Candidate/EmployeeSearch";

import {
  getAllVaccinations,
  createVaccinationRecord,
  updateVaccinationRecord,
} from "../../../../../api/services/ReferenceData/vaccination";
import { QK } from "../../../../../base/qk";

const ACCENT_BLUE = "#2196F3";
const DARK_TEXT = "#424242";

const VaccinationModal = ({ show, onHide, userType = "client", record = null }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [vaccinationName, setVaccinationName] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [givenDate, setGivenDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [isGiven, setIsGiven] = useState(false);

  // Populate state in edit mode
  useEffect(() => {
    if (record) {
      setVaccinationName(record.vaccination || "");
      setSelectedUser(record.client_info || record.candidate_info || null);
      setGivenDate(record.date_given || "");
      setDueDate(record.next_due_date || "");
      setNotes(record.notes || "");
      setIsGiven(record.is_given || false);
    } else {
      setVaccinationName("");
      setSelectedUser(null);
      setGivenDate("");
      setDueDate("");
      setNotes("");
      setIsGiven(false);
    }
  }, [record, show]);

  const { data: vaccinations = [], isLoading } = useQuery(QK.vaccinations, getAllVaccinations);
  const vaccinationOptions = vaccinations.map(v => ({ label: v.name, value: v.id }));

  // Unified mutation
  const saveMutation = useMutation(
    (data) => {
      // If editing, pass id inside object
      if (record?.id) return updateVaccinationRecord({ id: record.id, ...data });
      return createVaccinationRecord(data);
    },
    {
      onSuccess: () => {
        enqueueSnackbar(`Vaccination record ${record ? "updated" : "saved"}!`, { variant: "success" });
        queryClient.invalidateQueries(QK.vaccinationRecords);
        onHide();
      },
      onError: () => enqueueSnackbar(`Failed to ${record ? "update" : "save"} record`, { variant: "error" }),
    }
  );

  const handleSave = () => {
    if (!vaccinationName) return enqueueSnackbar("Select a vaccination", { variant: "warning" });
    if (!selectedUser?.id) return enqueueSnackbar("Select a user", { variant: "warning" });
    if (!givenDate) return enqueueSnackbar("Select date given", { variant: "warning" });

    const payload = {
      vaccination: vaccinationName,
      user_type: userType,
      user_id: selectedUser.id,
      date_given: givenDate,
      due_date: dueDate || null,
      notes: notes || "",
      is_given: isGiven,
    };

    saveMutation.mutate(payload);
  };

  const UserSelectComponent = userType === "client" ? ClientSelect : EmployeeSearch;
  const titleText = record ? "Edit Vaccination Record" : `Add ${userType === "client" ? "Client" : "Employee"} Vaccination Record`;

  return (
    <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#F9F9F9' }}>
        <Typography variant="h5" sx={{ color: DARK_TEXT, fontWeight: 'bold' }}>
          {titleText}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={56}>
                <CircularProgress size={24} color="primary" />
              </Box>
            ) : (
              <CustomSelect
                label="Vaccination Name*"
                size="md"
                selectedValue={vaccinationName}
                onChange={e => setVaccinationName(e.target.value)}
                options={vaccinationOptions}
                required
                fullWidth
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <UserSelectComponent
              label={userType === "client" ? "Select Service User*" : "Select Employee*"}
              value={selectedUser}
              onChange={setSelectedUser}
              required
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <CustomSwitch
              label="Vaccination Given"
              checked={isGiven}
              onChange={e => setIsGiven(e.target.checked)}
              sx={{ '& .MuiFormControlLabel-label': { color: DARK_TEXT } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomDate
              label="Date Given*"
              value={givenDate}
              onChange={e => setGivenDate(e.target.value)}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomDate
              label="Next Due Date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <CustomTextArea
              label="Notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          variant="outlined" 
          onClick={onHide} 
          sx={{ mr: 2, borderRadius: 2, borderColor: ACCENT_BLUE, color: ACCENT_BLUE }}
        >
          Close
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={saveMutation.isLoading}
          sx={{ borderRadius: 2, backgroundColor: ACCENT_BLUE, '&:hover': { backgroundColor: '#1565C0' } }}
        >
          {saveMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : record ? "Update" : "Save"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default VaccinationModal;